import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import PropTypes from 'prop-types';

/**
 * A text element that applies `text-truncate` and conditionally shows a
 * tooltip with the full value when the text is actually clipped. Short
 * values that fit without overflow do not get a redundant tooltip.
 *
 * Truncation is detected by comparing `scrollWidth` to `clientWidth` and
 * rechecked via ResizeObserver (catches container-level reflows, not just
 * window resize) and when the `text` prop changes.
 *
 * Usage:
 *   <TruncatedTextTooltip
 *       text={user.displayName}
 *       className="sidebar-profile-name"
 *   />
 */
export default function TruncatedTextTooltip({
    text,
    placement = 'top',
    className = '',
    as: Component = 'span',
    interactive = true,
}) {
    // A callback ref (backed by state) rather than a plain useRef: flipping
    // isTruncated switches the returned JSX between a bare Component and one
    // wrapped in OverlayTrigger — different element types at the same tree
    // position, so React unmounts the old DOM node and mounts a new one.
    // Keying the observer off the actual node in state (instead of a ref
    // object plus a dependency array that never changes across that swap)
    // means the effect re-runs whenever the node itself changes, for
    // whatever reason, rather than going stale after the first remount.
    const [node, setNode] = useState(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const checkTruncation = useCallback(() => {
        if (node) {
            setIsTruncated(node.scrollWidth > node.clientWidth);
        }
    }, [node]);

    useEffect(() => {
        if (!node) return undefined;
        checkTruncation();
        const observer = new ResizeObserver(checkTruncation);
        observer.observe(node);
        return () => observer.disconnect();
    }, [node, text, checkTruncation]);

    const truncatedClass = `text-truncate${className ? ` ${className}` : ''}`;

    if (!isTruncated) {
        return (
            <Component ref={setNode} className={truncatedClass}>
                {text}
            </Component>
        );
    }

    return (
        <OverlayTrigger
            placement={placement}
            trigger={interactive ? ['hover', 'focus'] : ['hover']}
            overlay={<Tooltip>{text}</Tooltip>}
        >
            <Component
                ref={setNode}
                className={truncatedClass}
                {...(interactive ? { tabIndex: 0 } : {})}
            >
                {text}
            </Component>
        </OverlayTrigger>
    );
}

TruncatedTextTooltip.propTypes = {
    /** The full text to display (and show in the tooltip when truncated). */
    text: PropTypes.string.isRequired,
    /** Tooltip placement relative to the element. */
    placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
    /** Additional CSS classes to apply alongside text-truncate. */
    className: PropTypes.string,
    /** The HTML element to render. Defaults to span. */
    as: PropTypes.elementType,
    /**
     * Whether the element should be keyboard-focusable and show the tooltip
     * on focus. Set to false when the component is already inside an
     * interactive parent (e.g. an anchor) to avoid nested tab stops.
     */
    interactive: PropTypes.bool,
};
